import { Navigate } from 'react-router-dom';
import React from 'react';
import { AuthProvider } from '@propelauth/react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import MainLayout from './dashboard/MainLayout';
import Orders from './orders/Orders';
import Calendar from './calendar/calendarWrapper/CalendarWrapper';
import { CalendarProvider } from './calendar/CalendarContext/CalendarContext';
import { CalendarFilterProvider } from './calendar/CalendarFilter/CalendarFilterContext';
import { AreasViewProvider } from './calendar/agendaView/AreasViewContext';
import { OrdersFilterProvider } from './orders/OrdersFilter/OrdersFilterContext';
import OrderPages from './orders/OrderPages';
import AuthGuard from './session/AuthGuard';
import InternalAuthProvider from './session/InternalAuthProvider';
import VouchersPage from './vouchers/VouchersPage';
import { ExternalVouchersPage } from './externalVouchers/ExternalVouchersPage';
import { VouchersFilterProvider } from './vouchers/VouchersFilter/VouchersFilterContext';
import { OrderPageSettingsPage } from './experienceSettings/OrderPageSettingsPage';
import { AccountSettingsPage } from './accountSettings/AccountSettingsPage';
import { EmailTemplatesPage } from './emailTemplates/EmailTemplatesPage';
import { SMSTemplatesPage } from './smsTemplates/SMSTemplatesPage';
import { WhatsAppSettingsPage } from './whatsappSettings/WhatsAppSettingsPage';
import { DiscountsPage } from './discounts/DiscountsPage';
import { CatalogPage } from './catalog/CatalogPage';
import { UsersPage } from './users/UsersPage';
import { UserPage } from './users/UserPage';
import { CreateExperiencePage } from './createExperiencePage/CreateExperiencePage';
import { ReportsPage } from './reports/ReportsPage';
import { PaymentsPage } from './payments/PaymentsPage';
import { GrowConnectPage } from './growConnect/GrowConnectPage';
import { WaitingListTable } from './waitingList/WaitingListTable';
import { PaymentLinkTable } from './paymentLinks/PaymentLinkTable';
import { QrBoardsPage } from './qrBoards/QrBoardsPage';
import { WidgetThemeProvider } from './common/theme/Theme';
import { UserSettingsPage } from './userSettings/UserSettingsPage';
import { PaperPage } from './common/PaperPage';
import { AnalyticsPage } from './orders/dashboards/AnalyticsPage';
import { ApiResponseHandlerProvider } from './common/apiNotifications/ApiResponseHandlerContext';
import { AuditLogPage } from './auditLog/AuditLogPage';
import { SvixPortalSettings } from './accountSettings/SvixPortalSettings';
import { StorefrontsOverviewPage } from './storefronts/StorefrontsOverviewPage';
import { BusinessDetailsPage } from './storefronts/BusinessDetailsPage';
import { CreatePassOrderPage } from './passes/orderPages/CreatePassOrderPage';
import { PassOrderPageSettingsPage } from './passes/orderPages/PassOrderPageSettingsPage';
import PassesPage from './passes/PassesPage';
import { OpenBillsPage } from './openBills/OpenBillsPage';
import { PassesFilterProvider } from './passes/PassesFilter/PassesFilterContext';

const routes = [
  {
    element: (
      <AuthProvider authUrl={import.meta.env.VITE_REACT_APP_AUTH_URL}>
        <InternalAuthProvider>
          <AuthGuard>
            <GoogleOAuthProvider clientId="432245203095-crb38f5c51ae987r0vkguv5o9h95gb85.apps.googleusercontent.com">
              <WidgetThemeProvider>
                <ApiResponseHandlerProvider>
                  <MainLayout />
                </ApiResponseHandlerProvider>
              </WidgetThemeProvider>
            </GoogleOAuthProvider>
          </AuthGuard>
        </InternalAuthProvider>
      </AuthProvider>
    ),
    children: [
      {
        path: '/dashboard/calendar/:startDate?',
        element: (
          <CalendarProvider>
            <CalendarFilterProvider>
              <AreasViewProvider>
                <Calendar />
              </AreasViewProvider>
            </CalendarFilterProvider>
          </CalendarProvider>
        ),
      },
      {
        path: '/dashboard/orders',
        element: (
          <CalendarProvider>
            <OrdersFilterProvider>
              <PaperPage>
                <Orders />
              </PaperPage>
            </OrdersFilterProvider>
          </CalendarProvider>
        ),
      },
      {
        path: '/dashboard/open-bills',
        element: (
          <CalendarProvider>
            <PaperPage>
              <OpenBillsPage />
            </PaperPage>
          </CalendarProvider>
        ),
      },
      {
        path: '/dashboard/vouchers',
        element: (
          <CalendarProvider>
            <VouchersFilterProvider>
              <VouchersPage />
            </VouchersFilterProvider>
          </CalendarProvider>
        ),
      },
      {
        path: '/dashboard/external-vouchers',
        element: (
          <CalendarProvider>
            <ExternalVouchersPage />
          </CalendarProvider>
        ),
      },
      {
        path: '/dashboard/discounts',
        element: (
          <PaperPage>
            <DiscountsPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/catalog',
        element: (
          <PaperPage>
            <CatalogPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/users',
        element: (
          <PaperPage>
            <UsersPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/users/:userId',
        element: (
          <PaperPage bare>
            <UserPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/create-experience',
        element: (
          <PaperPage>
            <CreateExperiencePage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/user-settings',
        element: (
          <PaperPage bare>
            <UserSettingsPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/account-settings',
        element: (
          <PaperPage bare>
            <AccountSettingsPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/email-templates',
        element: (
          <PaperPage bare>
            <EmailTemplatesPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/webhooks',
        element: (
          <PaperPage>
            <SvixPortalSettings />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/sms-templates',
        element: (
          <PaperPage>
            <SMSTemplatesPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/whatsapp-settings',
        element: (
          <PaperPage bare>
            <WhatsAppSettingsPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/settings/:orderPageId',
        element: (
          <PaperPage bare>
            <OrderPageSettingsPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/order-pages',
        element: (
          <PaperPage>
            <OrderPages />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/analytics',
        element: (
          <CalendarProvider>
            <PaperPage>
              <AnalyticsPage />
            </PaperPage>
          </CalendarProvider>
        ),
      },
      {
        path: '/dashboard/waiting-list',
        element: (
          <CalendarProvider>
            <PaperPage>
              <WaitingListTable />
            </PaperPage>
          </CalendarProvider>
        ),
      },
      {
        path: '/dashboard/payment-links',
        element: (
          <CalendarProvider>
            <PaperPage>
              <PaymentLinkTable />
            </PaperPage>
          </CalendarProvider>
        ),
      },
      {
        path: '/dashboard/qr-boards',
        element: (
          <PaperPage>
            <QrBoardsPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/reports',
        element: (
          <PaperPage bare>
            <ReportsPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/payments',
        element: (
          <PaperPage>
            <PaymentsPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/stripe',
        element: (
          <PaperPage>
            <PaymentsPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/grow-connect',
        element: (
          <PaperPage>
            <GrowConnectPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/audit-log',
        element: (
          <CalendarProvider>
            <PaperPage>
              <AuditLogPage />
            </PaperPage>
          </CalendarProvider>
        ),
      },
      {
        path: '/dashboard/storefronts',
        element: (
          <PaperPage>
            <StorefrontsOverviewPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/business-details',
        element: (
          <PaperPage bare>
            <BusinessDetailsPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/create-pass',
        element: (
          <PaperPage>
            <CreatePassOrderPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/pass-settings/:passOrderPageId',
        element: (
          <PaperPage bare>
            <PassOrderPageSettingsPage />
          </PaperPage>
        ),
      },
      {
        path: '/dashboard/passes',
        element: (
          <CalendarProvider>
            <PassesFilterProvider>
              <PaperPage>
                <PassesPage />
              </PaperPage>
            </PassesFilterProvider>
          </CalendarProvider>
        ),
      },
    ],
  },
  {
    path: '/',
    element: <Navigate to="/dashboard" />,
  },
  { path: '/dashboard', element: <Navigate to="/dashboard/calendar" /> },
  {
    path: '/dashboard/default',
    element: <Navigate to="/dashboard/calendar" />,
  },
  { path: '*', element: <div>Not Monkeybook</div> },
];

export default routes;
